import { IElement } from "core-integration/src/store/elements/types";
import { ITeam } from "core-integration/src/store/teams/types";
import { integerToMoney, isMoneyStat } from "core-integration/src/utils/money";
import { rgba, size } from "polished";
import * as React from "react";
import styled, { css } from "styled-components";
import MinusIcon from "../img/icons/minus.svg?react";
import PlusIcon from "../img/icons/plus.svg?react";
import ElementDialogButton from "./ElementDialogButton";
import ElementInTable from "./ElementInTable";
import { ElementCell, ElementRow } from "./ElementTable";

const MobileActionButton = styled.button`
  display: block;
  margin: auto;
  padding: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const IconStyles = css`
  ${size(18)};
  display: block;
  margin: auto;
  fill: currentColor;
`;

const AddPlayerIcon = styled(PlusIcon)<{ $canAddElement: boolean }>`
  ${IconStyles};
  color: ${({ theme }) => theme.colors.green};
  opacity: ${(props) => (props.$canAddElement ? "1" : "0.4")};
  cursor: ${(props) => (props.$canAddElement ? "pointer" : "not-allowed")};
`;

const RemovePlayerIcon = styled(MinusIcon)`
  ${IconStyles};
  color: ${({ theme }) => theme.colors.red};
`;

const MobileActionCell = styled.td`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const StyledElementListRow = styled(ElementRow)<IStyledProps>`
  ${(props) =>
    props.isProposed &&
    css`
      td,
      button {
        color: ${rgba("white", 0.6)};
      }

      img {
        opacity: 0.6;
      }
    `}
`;

const ElementListStatCell = styled.td`
  font-weight: bold;
`;

interface IStyledProps {
  isProposed: boolean;
}

interface IProps extends IStyledProps {
  currencyDivisor: number;
  element: IElement;
  handleAddElement: (elementId: number) => void;
  handleRemoveElement: (position: number) => void;
  hasRoomInPosition: boolean;
  proposedElementsById: { [key: string]: any };
  proposedPosition: number;
  renderElementDialog: () => void;
  renderElementMenu: () => void;
  sort: string | number;
  team: ITeam;
}

const ElementListRow: React.FC<IProps> = ({
  currencyDivisor,
  element,
  hasRoomInPosition,
  isProposed,
  proposedPosition,
  proposedElementsById,
  handleAddElement,
  handleRemoveElement,
  renderElementMenu,
  sort,
  team,
}) => {
  const canAddElement = Boolean(
    Object.keys(proposedElementsById).length < 15 && hasRoomInPosition,
  );

  return (
    <StyledElementListRow isProposed={isProposed}>
      <td>
        <ElementDialogButton elementId={element.id} variant="list" />
      </td>
      <ElementCell>
        <ElementInTable
          renderElementMenu={renderElementMenu}
          element={element}
          team={team}
        />
      </ElementCell>
      <ElementListStatCell>
        {integerToMoney(element.now_cost, currencyDivisor)}
      </ElementListStatCell>
      <ElementListStatCell>
        {isMoneyStat(sort)
          ? integerToMoney(element[sort] as number, currencyDivisor)
          : element[sort]}
      </ElementListStatCell>
      {isProposed ? (
        <MobileActionCell>
          <MobileActionButton
            onClick={() => handleRemoveElement(proposedPosition)}
          >
            <RemovePlayerIcon />
          </MobileActionButton>
        </MobileActionCell>
      ) : (
        <MobileActionCell>
          <MobileActionButton
            disabled={!canAddElement}
            onClick={() => handleAddElement(element.id)}
          >
            <AddPlayerIcon $canAddElement={canAddElement} />
          </MobileActionButton>
        </MobileActionCell>
      )}
    </StyledElementListRow>
  );
};

export default ElementListRow;
